import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AddHouseModal } from "@features/AddHouseModal/AddHouseModal";
import { Button } from "@components/Button";
import { DocumentArrowIcon, HouseIcon, HousePlusIcon } from "@components/icons";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Input } from "@components/inputs/Input/Input";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { useOverviewQuery } from "@services/overview/queries";
import { type IBillData } from "@services/overview/types";
import { useTransferServiceProviderMutation } from "@services/serviceProviders/mutations";
import {
  useTransferServiceProviderSchema,
  type TTransferServiceProviderSchema,
} from "./useTransferServiceProviderSchema";

interface ITransferServiceProviderModalProps extends IUseModalProps {
  billData: IBillData;
  jointBillServiceId: number;
}

export const TransferServiceProviderModal: React.FC<
  ITransferServiceProviderModalProps
> = (props) => {
  const { billData, jointBillServiceId, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();
  const { create } = useModalStore();

  const { data: overviewData } = useOverviewQuery();

  const jointBillService = overviewData?.jointBillServices.find(
    (jbs) => jbs.jointBillServiceId === jointBillServiceId,
  );

  const availableHouses = overviewData?.jointBillServices
    .map((jointBillService) => ({
      value: jointBillService.jointBillServiceId,
      label: jointBillService.displayName ?? t("common.defaultAddress"),
    }))
    .filter((house) => house.value !== jointBillServiceId);

  const schema = useTransferServiceProviderSchema();

  const { control, handleSubmit, setError, setValue } =
    useForm<TTransferServiceProviderSchema>({
      values: {
        transferToJointBillServiceId: availableHouses?.[0]?.value ?? 0,
      },
      resolver: zodResolver(schema),
    });

  const { mutateAsync, isPending } = useTransferServiceProviderMutation();
  const { setApiErrors } = useApiErrorUtils(setError);

  const onSubmit: SubmitHandler<TTransferServiceProviderSchema> = async (
    data,
  ) => {
    const response = await mutateAsync({
      fromJointBillServiceId: jointBillServiceId,
      jointBillServiceAgreementId: billData.jointBillServiceAgreementId,
      toJointBillServiceId: data.transferToJointBillServiceId,
    }).catch(setApiErrors);

    if (response) {
      toast.success(t("transferBillModal.toast.success"));
      modal.close();
    }
  };

  const openAddHouseModal = () =>
    create((modalProps) => (
      <AddHouseModal
        {...modalProps}
        onSuccess={(newJointBillService) => {
          setValue("transferToJointBillServiceId", newJointBillService);
        }}
      />
    ));

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: DocumentArrowIcon,
      }}
      title={t("transferBillModal.title")}
      subtitle={t("transferBillModal.subtitle")}
      actionButtons={
        <React.Fragment>
          <Button
            variant="secondary"
            onClick={() => {
              modal.close();
            }}
            className="w-full"
          >
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("common.form.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <form className="flex flex-col gap-4">
        <Input
          label={t("transferBillModal.fields.serviceProviderName")}
          value={billData.displayName}
          disabled
        />
        <Input
          label={t("transferBillModal.fields.currentAddress")}
          value={jointBillService?.displayName ?? t("common.defaultAddress")}
          disabled
        />
        <FormSelect
          name="transferToJointBillServiceId"
          control={control}
          icon={HouseIcon}
          options={availableHouses ?? []}
          label={t("transferBillModal.fields.transferTo")}
        />
        <Button
          variant="secondary"
          icon={HousePlusIcon}
          className="w-full"
          onClick={openAddHouseModal}
          type="button"
        >
          {t("transferBillModal.addHouse")}
        </Button>
      </form>
    </Modal>
  );
};
